import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ElectricBolt, ElectricScooter, EnergySavingsLeaf, ShieldOutlined } from "@mui/icons-material";
import loginBg from "../assets/vehicle-icons/refresh-bg.png";

const featureItems = [
  { icon: <ElectricBolt />, title: "Smart Fleet", sub: "Management" },
  { icon: <ElectricScooter />, title: "Real-time", sub: "Monitoring" },
  { icon: <ShieldOutlined />, title: "Secure &", sub: "Reliable" },
  { icon: <EnergySavingsLeaf />, title: "Sustainable", sub: "Future" },
];

const loadingDots = Array.from({ length: 8 });

export default function EV91Loader({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 2200);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="ev91-loader"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.55 }}
          style={{
            height: "100dvh",
            width: "100%",
            overflow: "hidden",
            background: "#020913",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: "100%",
              display: "grid",
              placeItems: "center",
              px: { xs: 2, sm: 3 },
              backgroundImage: `
                linear-gradient(180deg, rgba(1, 8, 16, 0.5) 0%, rgba(1, 8, 16, 0.72) 57%, rgba(1, 8, 16, 0.94) 100%),
                radial-gradient(circle at 50% 37%, rgba(34, 236, 124, 0.2), transparent 22%),
                url(${loginBg})
              `,
              backgroundSize: "cover",
              backgroundPosition: { xs: "center", md: "center" },
              color: "#fff",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: { xs: 8, md: 14 },
                border: "1px solid rgba(67, 168, 255, 0.24)",
                borderRadius: { xs: 3, md: 4 },
                pointerEvents: "none",
                boxShadow: "inset 0 0 70px rgba(5, 160, 255, 0.1)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, rgba(1, 8, 16, 0.72), transparent 24%, transparent 76%, rgba(1, 8, 16, 0.78))",
                pointerEvents: "none",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                width: "min(920px, 92vw)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                transform: { xs: "translateY(-8px)", md: "translateY(18px)" },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 180, sm: 230, md: 300 },
                  height: { xs: 180, sm: 230, md: 300 },
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                  style={{
                    position: "absolute",
                    inset: isXs ? 12 : 18,
                    borderRadius: "50%",
                    border: "2px solid rgba(34, 238, 124, 0.82)",
                    borderTopColor: "rgba(34, 238, 124, 0.18)",
                    borderRightColor: "#20f183",
                    boxShadow: "0 0 28px rgba(34, 238, 124, 0.26)",
                  }}
                />
                <motion.div
                  animate={{ scale: [1, 1.04, 1], opacity: [0.45, 0.82, 0.45] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                  style={{
                    position: "absolute",
                    inset: isXs ? 28 : 42,
                    borderRadius: "50%",
                    border: "1px dotted rgba(34, 238, 124, 0.65)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: { xs: 36, sm: 48, md: 64 },
                    borderRadius: "50%",
                    background: "rgba(3, 11, 20, 0.72)",
                    boxShadow: "inset 0 0 36px rgba(34, 238, 124, 0.12)",
                  }}
                />
                <Box sx={{ position: "relative", textAlign: "center" }}>
                  <Typography
                    component="h1"
                    sx={{
                      m: 0,
                      fontSize: { xs: "2.7rem", sm: "3.7rem", md: "4.7rem" },
                      lineHeight: 1,
                      fontStyle: "italic",
                      fontWeight: 900,
                      letterSpacing: 0,
                      textShadow: "0 0 24px rgba(34, 238, 124, 0.24)",
                    }}
                  >
                    EV<Box component="span" sx={{ color: "#28ef7b" }}>91</Box>
                  </Typography>
                  <ElectricBolt
                    sx={{
                      mt: { xs: 1, md: 1.5 },
                      fontSize: { xs: 42, sm: 52, md: 66 },
                      color: "#28ef7b",
                      filter: "drop-shadow(0 0 16px rgba(40, 239, 123, 0.8))",
                    }}
                  />
                </Box>
              </Box>

              <Typography
                sx={{
                  mt: { xs: 1, md: 1.5 },
                  fontSize: { xs: "1.15rem", sm: "1.65rem", md: "2rem" },
                  fontWeight: 800,
                  letterSpacing: { xs: 1.2, md: 2.5 },
                  textAlign: "center",
                  textTransform: "uppercase",
                  textShadow: "0 8px 24px rgba(0, 0, 0, 0.65)",
                }}
              >
                Initializing{" "}
                <Box component="span" sx={{ color: "#22ef7a" }}>
                  Dashboard
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  color: "#d6dde8",
                  fontSize: { xs: "0.88rem", sm: "1rem", md: "1.08rem" },
                  textAlign: "center",
                }}
              >
                Preparing your smart fleet experience...
              </Typography>

              <Box sx={{ display: "flex", gap: 1.1, mt: { xs: 2.2, md: 3 } }}>
                {loadingDots.map((_, index) => (
                  <motion.span
                    key={index}
                    animate={{ opacity: [0.25, 1, 0.25], scale: [0.8, 1, 0.8] }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      delay: index * 0.12,
                    }}
                    style={{
                      width: isXs ? 7 : 9,
                      height: isXs ? 7 : 9,
                      borderRadius: "50%",
                      background: index < 3 ? "#22ef7a" : "rgba(126, 149, 170, 0.42)",
                      boxShadow: index < 3 ? "0 0 12px rgba(34, 239, 122, 0.72)" : "none",
                    }}
                  />
                ))}
              </Box>

              <Box
                sx={{
                  mt: { xs: 4, md: 7 },
                  width: "min(780px, 100%)",
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  background: "rgba(4, 14, 25, 0.72)",
                  border: "1px solid rgba(117, 153, 177, 0.2)",
                  borderRadius: 3,
                  backdropFilter: "blur(18px)",
                  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
                  overflow: "hidden",
                }}
              >
                {featureItems.map((item, index) => (
                  <Box
                    key={item.title}
                    sx={{
                      minHeight: 82,
                      display: "grid",
                      gridTemplateColumns: "42px 1fr",
                      alignItems: "center",
                      gap: 1.5,
                      px: { sm: 2, md: 3 },
                      borderLeft: index === 0 ? "none" : "1px solid rgba(117, 153, 177, 0.18)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "#23f07b",
                        border: "1px solid rgba(35, 240, 123, 0.72)",
                        boxShadow: "0 0 18px rgba(35, 240, 123, 0.18)",
                        "& svg": { fontSize: 23 },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { sm: "0.9rem", md: "1rem" } }}>
                      {item.title}
                      <Box component="span" sx={{ display: "block", color: "#d9e1ec", fontWeight: 500 }}>
                        {item.sub}
                      </Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </motion.div>
      ) : (
        children
      )}
    </AnimatePresence>
  );
}
